/**
 * CreditsTab Component
 * Credit balance, transaction history, and usage summary
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Coins, History, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { GlassCard, GlassCardContent, CollapsibleGlassCard } from '@/components/common/GlassCard';
import { Button } from '@/components/ui/button';
import { useCreditBalance, useCreditHistory, useUsageSummary } from '@/hooks/useCredits';
import { BuyCreditsDialog } from '@/components/credits/BuyCreditsDialog';

const PAGE_SIZE = 10;

export function CreditsTab() {
  const { t } = useTranslation();
  const [historyPage, setHistoryPage] = useState(0);
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);

  const { data: balance, isLoading: balanceLoading } = useCreditBalance();
  const { data: history, isLoading: historyLoading } = useCreditHistory(PAGE_SIZE, historyPage * PAGE_SIZE);
  const { data: usage, isLoading: usageLoading } = useUsageSummary();

  return (
    <div className="space-y-6">
      {/* Credit Balance Card */}
      <GlassCard>
        <GlassCardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--color-primary-start)] to-[var(--color-primary-end)] flex items-center justify-center">
              <Coins className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('profile.credits.balance')}</p>
              <p className="text-2xl font-bold text-[var(--color-charcoal)]">
                {balanceLoading ? '-' : balance?.credits ?? 0}
              </p>
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Buy Credits Button */}
      <div className="flex justify-end">
        <Button className="btn-primary" onClick={() => setBuyCreditsOpen(true)}>
          {t('profile.credits.buyCredits')}
        </Button>
      </div>

      {/* Usage Summary */}
      {usage && !usageLoading && (
        <CollapsibleGlassCard title={t('profile.credits.usageSummary')}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500">{t('profile.credits.totalRequests')}</p>
              <p className="text-lg font-semibold">{usage.totalRequests}</p>
            </div>
            <div>
              <p className="text-gray-500">{t('profile.credits.creditsUsed')}</p>
              <p className="text-lg font-semibold">{usage.creditsUsed}</p>
            </div>
            <div>
              <p className="text-gray-500">{t('profile.credits.creditsRemaining')}</p>
              <p className="text-lg font-semibold">{usage.creditsRemaining}</p>
            </div>
          </div>
        </CollapsibleGlassCard>
      )}

      {/* Transaction History */}
      <CollapsibleGlassCard
        title={
          <span className="flex items-center gap-2">
            <History className="w-5 h-5" />
            {t('profile.credits.history')}
          </span>
        }
        defaultOpen={false}
      >
          {historyLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : !history?.transactions?.length ? (
            <p className="text-gray-500 text-sm py-4 text-center">
              {t('profile.credits.noTransactions')}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-3 px-2 font-medium text-gray-500">{t('profile.credits.transactionType')}</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-500">{t('profile.credits.creditType')}</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">{t('profile.credits.amount')}</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">{t('profile.credits.balanceAfter')}</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-500">{t('profile.credits.reason')}</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">{t('profile.credits.date')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.transactions.map((tx) => (
                      <tr key={tx.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="py-3 px-2">{tx.transactionType}</td>
                        <td className="py-3 px-2">{tx.creditType}</td>
                        <td className={`py-3 px-2 text-right font-medium ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {tx.amount >= 0 ? '+' : ''}{tx.amount}
                        </td>
                        <td className="py-3 px-2 text-right">{tx.balanceAfter}</td>
                        <td className="py-3 px-2 text-gray-600">{tx.reason}</td>
                        <td className="py-3 px-2 text-right text-gray-500">
                          {new Date(tx.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {history.total > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                    disabled={historyPage === 0}
                    className="gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    {t('profile.credits.previous')}
                  </Button>
                  <span className="text-sm text-gray-500">
                    {historyPage + 1} / {Math.ceil(history.total / PAGE_SIZE)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setHistoryPage((p) => p + 1)}
                    disabled={(historyPage + 1) * PAGE_SIZE >= history.total}
                    className="gap-1"
                  >
                    {t('profile.credits.next')}
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
      </CollapsibleGlassCard>

      <BuyCreditsDialog isOpen={buyCreditsOpen} onClose={() => setBuyCreditsOpen(false)} />
    </div>
  );
}
